import { gql } from "@apollo/client";
import { EReceiptPrinterPrinterType } from "../model/model";
import { ORDER_FIELDS_FRAGMENT } from "./customFragments";

export enum EOrderStatus {
    NEW = "NEW",
    COMPLETED = "COMPLETED",
    CANCELLED = "CANCELLED",
    REFUNDED = "REFUNDED",
    PARKED = "PARKED",
}

export const GET_URL_REDIRECT_BY_ORIGINAL_URL = gql`
    query GetUrlRedirectByOriginalUrl($originalUrl: String!, $limit: Int) {
        getUrlRedirectByOriginalUrl(originalUrl: $originalUrl, limit: $limit) {
            items {
                id
                originalUrl
                redirectUrl
                restaurantId
            }
            nextToken
        }
    }
`;

export const LIST_VERIFIED_RESTAURANTS = gql`
    query ListVerifiedRestaurants($filter: ModelRestaurantFilterInput, $limit: Int, $nextToken: String) {
        listRestaurants(filter: $filter, limit: $limit, nextToken: $nextToken) {
            items {
                id
                name
                verified
            }
            nextToken
        }
    }
`;

export enum EOrderType {
    DINEIN = "DINEIN",
    TAKEAWAY = "TAKEAWAY",
    PICKUP = "PICKUP",
    DELIVERY = "DELIVERY",
}

export enum ERegisterType {
    KIOSK = "KIOSK",
    POS = "POS",
    ONLINE = "ONLINE",
}

export enum ERegisterPrinterType {
    BLUETOOTH = "BLUETOOTH",
    WIFI = "WIFI",
    USB = "USB",
}

export const GET_USER = gql`
    query GetUser($userId: ID!) {
        getUser(id: $userId) {
            id
            identityPoolId
            firstName
            lastName
            email
            restaurants {
                items {
                    id
                    name
                    advertisements {
                        items {
                            id
                            name
                            content {
                                key
                                bucket
                                region
                                identityPoolId
                            }
                        }
                    }
                    registers {
                        items {
                            id
                            active
                            name
                            enableTableFlags
                            enableCovers
                            enableBuzzerNumbers
                            enablePayLater
                            enableCashPayments
                            enableEftposPayments
                            enableUberEatsPayments
                            enableMenulogPayments
                            availableOrderTypes
                            type
                            hideMostPopularCategory
                            orderBatchWindowInSeconds
                            eftposProvider
                            eftposIpAddress
                            eftposPortNumber
                            windcaveStationId
                            windcaveStationUser
                            windcaveStationKey
                            skipEftposReceiptSignature
                            orderNumberSuffix
                            orderNumberStart
                            surchargePercentage
                            defaultCategoryView
                            preSelectedProducts
                            customStyleSheet {
                                key
                                bucket
                                region
                                identityPoolId
                            }
                            printers {
                                items {
                                    id
                                    name
                                    type
                                    printerType
                                    address
                                    receiptFooterText
                                    customerPrinter
                                    kitchenPrinter
                                    kitchenPrinterSmall
                                    kitchenPrinterLarge
                                    printAllOrderReceipts
                                    printOnlineOrderReceipts
                                    ignoreCategories(limit: 500) {
                                        items {
                                            id
                                            category {
                                                id
                                                name
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`;

export interface IGET_USER {
    id: string;
    identityPoolId: string;
    firstName: string;
    lastName: string;
    email: string;
    restaurants: {
        items: IGET_USER_RESTAURANT[];
    };
}

export interface IGET_USER_RESTAURANT {
    id: string;
    name: string;
}

export interface IGET_USER_REGISTER_PRINTER {
    id: string;
    name: string;
    type: ERegisterPrinterType;
    printerType: EReceiptPrinterPrinterType;
    address: string;
    receiptFooterText: string | null;
    customerPrinter: boolean;
    kitchenPrinter: boolean;
    kitchenPrinterSmall: boolean;
    kitchenPrinterLarge: boolean;
    ignoreCategories: {
        items: IGET_USER_REGISTER_PRINTER_IGNORE_CATEGORY[];
    };
    ignoreProducts: {
        items: IGET_USER_REGISTER_PRINTER_IGNORE_PRODUCT[];
    };
}

export interface IGET_USER_REGISTER_PRINTER_IGNORE_CATEGORY {
    id: string;
    category: {
        id: string;
        name: string;
    };
}

export interface IGET_USER_REGISTER_PRINTER_IGNORE_PRODUCT {
    id: string;
    product: {
        id: string;
        name: string;
    };
}

export const GET_RESTAURANT = gql`
    query GetRestaurant($restaurantId: ID!) {
        getRestaurant(id: $restaurantId) {
            id
            name
            description
            country
            isAcceptingOrders
            verified
            restaurantManagerId
            stripeAccountId
            address {
                formattedAddress
                aptSuite
                streetAddress
                city
                state
                zipCode
                country
                pickupNotes
            }
            _geoloc {
                lat
                lng
            }
            pickupPhoneNumber
            operatingHours {
                monday {
                    openingTime
                    closingTime
                    totalNumberOfOrdersThreshold
                    totalNumberOfOrdersThresholdPerTimeSlot
                    maxCoversPerTimeSlot
                }
                tuesday {
                    openingTime
                    closingTime
                    totalNumberOfOrdersThreshold
                    totalNumberOfOrdersThresholdPerTimeSlot
                    maxCoversPerTimeSlot
                }
                wednesday {
                    openingTime
                    closingTime
                    totalNumberOfOrdersThreshold
                    totalNumberOfOrdersThresholdPerTimeSlot
                    maxCoversPerTimeSlot
                }
                thursday {
                    openingTime
                    closingTime
                    totalNumberOfOrdersThreshold
                    totalNumberOfOrdersThresholdPerTimeSlot
                    maxCoversPerTimeSlot
                }
                friday {
                    openingTime
                    closingTime
                    totalNumberOfOrdersThreshold
                    totalNumberOfOrdersThresholdPerTimeSlot
                    maxCoversPerTimeSlot
                }
                saturday {
                    openingTime
                    closingTime
                    totalNumberOfOrdersThreshold
                    totalNumberOfOrdersThresholdPerTimeSlot
                    maxCoversPerTimeSlot
                }
                sunday {
                    openingTime
                    closingTime
                    totalNumberOfOrdersThreshold
                    totalNumberOfOrdersThresholdPerTimeSlot
                    maxCoversPerTimeSlot
                }
            }
            logo {
                key
                bucket
                region
                identityPoolId
            }
            gstNumber
            customStyleSheet {
                key
                bucket
                region
                identityPoolId
            }
            enableOnlineDelivery
            restaurantManagedDeliveryFeeBands {
                minmumDistanceMeters
                maxmumDistanceMeters
                deliveryProvider
                fee
            }
            onlinePaymentGatewayProvider
            onlineDeliveryRestaurantContributionPercentage
            onlineDeliveryRestaurantContributionStatic
            onlineDeliveryTabinContributionPercentage
            onlineDeliveryTabinContributionStatic
            onlineDeliveryContributionMinimumAmount
            autoCompleteOrders
            preparationTimeInMinutes
            delayBetweenOrdersInSeconds
            pauseOnlineOrdersUntil
            pauseReservationsUntil
            orderThresholdMessage
            surchargePercentage
            salesReportMailingList
            orderThresholds {
                enable
            }
            advertisements {
                items {
                    id
                    name
                    content {
                        key
                        bucket
                        region
                        identityPoolId
                    }
                    availability {
                        monday {
                            startTime
                            endTime
                        }
                        tuesday {
                            startTime
                            endTime
                        }
                        wednesday {
                            startTime
                            endTime
                        }
                        thursday {
                            startTime
                            endTime
                        }
                        friday {
                            startTime
                            endTime
                        }
                        saturday {
                            startTime
                            endTime
                        }
                        sunday {
                            startTime
                            endTime
                        }
                    }
                }
            }
            thirdPartyIntegrations {
                enable
                awaitThirdPartyResponse
                shift8 {
                    enable
                    storeApiUrl
                    storeUuid
                    storeLocationNumber
                }
                wizBang {
                    enable
                    storeApiUrl
                    username
                    password
                }
                doshii {
                    enable
                    locationId
                }
            }
            upSellCrossSell {
                id
                customCategories {
                    items {
                        id
                    }
                }
                customProducts {
                    items {
                        id
                        categories {
                            items {
                                category {
                                    id
                                }
                            }
                        }
                    }
                }
            }
            registers {
                items {
                    id
                    active
                    name
                    enableTableFlags
                    enableCovers
                    enableBuzzerNumbers
                    enableSkuScanner
                    checkConditionsBeforeCreateOrder
                    enablePayLater
                    enableCashPayments
                    enableEftposPayments
                    enableUberEatsPayments
                    enableMenulogPayments
                    availableOrderTypes
                    type
                    requestCustomerInformation {
                        firstName
                        email
                        phoneNumber
                        signature
                    }
                    hideMostPopularCategory
                    orderBatchWindowInSeconds
                    eftposProvider
                    eftposIpAddress
                    eftposPortNumber
                    windcaveStationId
                    windcaveStationUser
                    windcaveStationKey
                    skipEftposReceiptSignature
                    askToPrintCustomerReceipt
                    orderNumberSuffix
                    orderNumberStart
                    surchargePercentage
                    defaultCategoryView
                    preSelectedProducts
                    customStyleSheet {
                        key
                        bucket
                        region
                        identityPoolId
                    }
                    printers {
                        items {
                            id
                            name
                            type
                            printerType
                            address
                            receiptFooterText
                            customerPrinter
                            kitchenPrinter
                            kitchenPrinterSmall
                            kitchenPrinterLarge
                            hidePreparationTime
                            hideModifierGroupName
                            printAllOrderReceipts
                            printOnlineOrderReceipts
                            ignoreCategories(limit: 500) {
                                items {
                                    id
                                    category {
                                        id
                                        name
                                    }
                                }
                            }
                            ignoreProducts(limit: 500) {
                                items {
                                    id
                                    product {
                                        id
                                        name
                                    }
                                }
                            }
                        }
                    }
                }
            }
            promotions {
                items {
                    id
                    name
                    type
                    code
                    autoApply
                    startDate
                    endDate
                    availability {
                        monday {
                            startTime
                            endTime
                        }
                        tuesday {
                            startTime
                            endTime
                        }
                        wednesday {
                            startTime
                            endTime
                        }
                        thursday {
                            startTime
                            endTime
                        }
                        friday {
                            startTime
                            endTime
                        }
                        saturday {
                            startTime
                            endTime
                        }
                        sunday {
                            startTime
                            endTime
                        }
                    }
                    availablePlatforms
                    availableOrderTypes
                    totalNumberUsed
                    totalAvailableUses
                    minSpend
                    applyToCheapest
                    applyToModifiers
                    maxApplicationsPerOrder
                    items {
                        items {
                            id
                            minQuantity
                            categoryIds
                            productIds
                        }
                    }
                    discounts {
                        items {
                            id
                            amount
                            type
                            items {
                                items {
                                    id
                                    minQuantity
                                    categoryIds
                                    productIds
                                }
                            }
                        }
                    }
                }
            }
            categories(limit: 20) {
                items {
                    id
                    name
                    kitchenName
                    description
                    soldOut
                    soldOutDate
                    imageUrl
                    image {
                        key
                        bucket
                        region
                        identityPoolId
                    }
                    displaySequence
                    availablePlatforms
                    availableOrderTypes
                    availability {
                        monday {
                            startTime
                            endTime
                        }
                        tuesday {
                            startTime
                            endTime
                        }
                        wednesday {
                            startTime
                            endTime
                        }
                        thursday {
                            startTime
                            endTime
                        }
                        friday {
                            startTime
                            endTime
                        }
                        saturday {
                            startTime
                            endTime
                        }
                        sunday {
                            startTime
                            endTime
                        }
                    }
                    products(limit: 100) {
                        items {
                            id
                            displaySequence
                            product {
                                id
                                name
                                kitchenName
                                description
                                deliveryPrice
                                price
                                displayPrice
                                tags
                                totalQuantitySold
                                totalQuantityAvailable
                                soldOut
                                soldOutDate
                                imageUrl
                                image {
                                    key
                                    bucket
                                    region
                                    identityPoolId
                                }
                                availablePlatforms
                                availableOrderTypes
                                availability {
                                    monday {
                                        startTime
                                        endTime
                                    }
                                    tuesday {
                                        startTime
                                        endTime
                                    }
                                    wednesday {
                                        startTime
                                        endTime
                                    }
                                    thursday {
                                        startTime
                                        endTime
                                    }
                                    friday {
                                        startTime
                                        endTime
                                    }
                                    saturday {
                                        startTime
                                        endTime
                                    }
                                    sunday {
                                        startTime
                                        endTime
                                    }
                                }
                                subCategories
                                reportingGroup
                                categories {
                                    items {
                                        category {
                                            id
                                            name
                                            kitchenName
                                            imageUrl
                                            image {
                                                key
                                                bucket
                                                region
                                                identityPoolId
                                            }
                                            displaySequence
                                            availablePlatforms
                                            availableOrderTypes
                                            availability {
                                                monday {
                                                    startTime
                                                    endTime
                                                }
                                                tuesday {
                                                    startTime
                                                    endTime
                                                }
                                                wednesday {
                                                    startTime
                                                    endTime
                                                }
                                                thursday {
                                                    startTime
                                                    endTime
                                                }
                                                friday {
                                                    startTime
                                                    endTime
                                                }
                                                saturday {
                                                    startTime
                                                    endTime
                                                }
                                                sunday {
                                                    startTime
                                                    endTime
                                                }
                                            }
                                        }
                                    }
                                }
                                modifierGroups(limit: 20) {
                                    items {
                                        id
                                        displaySequence
                                        modifierGroup {
                                            id
                                            name
                                            kitchenName
                                            choiceMin
                                            choiceMax
                                            choiceDuplicate
                                            collapsedByDefault
                                            hideForCustomer
                                            availablePlatforms
                                            availableOrderTypes
                                            modifiers(limit: 50) {
                                                items {
                                                    id
                                                    displaySequence
                                                    modifier {
                                                        preSelectedQuantity
                                                        id
                                                        name
                                                        kitchenName
                                                        description
                                                        deliveryPrice
                                                        price
                                                        imageUrl
                                                        image {
                                                            key
                                                            bucket
                                                            region
                                                            identityPoolId
                                                        }
                                                        totalQuantitySold
                                                        totalQuantityAvailable
                                                        soldOut
                                                        soldOutDate
                                                        availablePlatforms
                                                        availableOrderTypes
                                                        subModifierGroups
                                                        productModifier {
                                                            id
                                                            name
                                                            kitchenName
                                                            description
                                                            deliveryPrice
                                                            price
                                                            tags
                                                            totalQuantitySold
                                                            totalQuantityAvailable
                                                            soldOut
                                                            soldOutDate
                                                            imageUrl
                                                            image {
                                                                key
                                                                bucket
                                                                region
                                                                identityPoolId
                                                            }
                                                            availablePlatforms
                                                            availableOrderTypes
                                                            categories {
                                                                items {
                                                                    category {
                                                                        id
                                                                        name
                                                                        kitchenName
                                                                        imageUrl
                                                                        image {
                                                                            key
                                                                            bucket
                                                                            region
                                                                            identityPoolId
                                                                        }
                                                                    }
                                                                }
                                                            }
                                                            availability {
                                                                monday {
                                                                    startTime
                                                                    endTime
                                                                }
                                                                tuesday {
                                                                    startTime
                                                                    endTime
                                                                }
                                                                wednesday {
                                                                    startTime
                                                                    endTime
                                                                }
                                                                thursday {
                                                                    startTime
                                                                    endTime
                                                                }
                                                                friday {
                                                                    startTime
                                                                    endTime
                                                                }
                                                                saturday {
                                                                    startTime
                                                                    endTime
                                                                }
                                                                sunday {
                                                                    startTime
                                                                    endTime
                                                                }
                                                            }
                                                            modifierGroups(limit: 20) {
                                                                items {
                                                                    id
                                                                    displaySequence
                                                                    modifierGroup {
                                                                        id
                                                                        name
                                                                        kitchenName
                                                                        choiceMin
                                                                        choiceMax
                                                                        choiceDuplicate
                                                                        collapsedByDefault
                                                                        hideForCustomer
                                                                        availablePlatforms
                                                                        availableOrderTypes
                                                                        modifiers(limit: 50) {
                                                                            items {
                                                                                id
                                                                                displaySequence
                                                                                modifier {
                                                                                    preSelectedQuantity
                                                                                    id
                                                                                    name
                                                                                    kitchenName
                                                                                    deliveryPrice
                                                                                    price
                                                                                    imageUrl
                                                                                    image {
                                                                                        key
                                                                                        bucket
                                                                                        region
                                                                                        identityPoolId
                                                                                    }
                                                                                    productModifier {
                                                                                        id
                                                                                        name
                                                                                        kitchenName
                                                                                        description
                                                                                        deliveryPrice
                                                                                        price
                                                                                        tags
                                                                                        totalQuantitySold
                                                                                        totalQuantityAvailable
                                                                                        soldOut
                                                                                        soldOutDate
                                                                                        imageUrl
                                                                                        image {
                                                                                            key
                                                                                            bucket
                                                                                            region
                                                                                            identityPoolId
                                                                                        }
                                                                                        availablePlatforms
                                                                                        availableOrderTypes
                                                                                        categories {
                                                                                            items {
                                                                                                category {
                                                                                                    id
                                                                                                    name
                                                                                                    kitchenName
                                                                                                    imageUrl
                                                                                                    image {
                                                                                                        key
                                                                                                        bucket
                                                                                                        region
                                                                                                        identityPoolId
                                                                                                    }
                                                                                                }
                                                                                            }
                                                                                        }
                                                                                        availability {
                                                                                            monday {
                                                                                                startTime
                                                                                                endTime
                                                                                            }
                                                                                            tuesday {
                                                                                                startTime
                                                                                                endTime
                                                                                            }
                                                                                            wednesday {
                                                                                                startTime
                                                                                                endTime
                                                                                            }
                                                                                            thursday {
                                                                                                startTime
                                                                                                endTime
                                                                                            }
                                                                                            friday {
                                                                                                startTime
                                                                                                endTime
                                                                                            }
                                                                                            saturday {
                                                                                                startTime
                                                                                                endTime
                                                                                            }
                                                                                            sunday {
                                                                                                startTime
                                                                                                endTime
                                                                                            }
                                                                                        }
                                                                                    }
                                                                                    totalQuantitySold
                                                                                    totalQuantityAvailable
                                                                                    soldOut
                                                                                    soldOutDate
                                                                                    availablePlatforms
                                                                                }
                                                                            }
                                                                        }
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
            # products(limit: 500) {
            #     items {
            #         id
            #         name
            #         soldOut
            #         soldOutDate
            #         totalQuantityAvailable
            #     }
            # }
            # # Only for stock component
            # modifiers(limit: 500) {
            #     items {
            #         id
            #         name
            #         soldOut
            #         soldOutDate
            #         totalQuantityAvailable
            #         productModifier {
            #             id
            #             name
            #             deliveryPrice
            #             price
            #         }
            #     }
            # }
        }
    }
`;

export interface IGET_RESTAURANT {
    id: string;
    name: string;
    description: string;
    country: string;
    isAcceptingOrders: boolean;
    verified: boolean;
    restaurantManagerId: string;
    stripeAccountId: string;
    address: {
        formattedAddress: string;
        aptSuite: string;
        streetAddress: string;
        city: string;
        state: string;
        zipCode: string;
        country: string;
        pickupNotes: string;
    };
    _geoloc: {
        lat: number;
        lng: number;
    };
    pickupPhoneNumber: string;
    operatingHours: IGET_RESTAURANT_OPERATING_HOURS;
    logo?: IS3Object;
    gstNumber: string | null;
    customStyleSheet?: IS3Object;
    enableOnlineDelivery?: boolean | null;
    restaurantManagedDeliveryFeeBands?: {
        minmumDistanceMeters: number;
        maxmumDistanceMeters: number;
        deliveryProvider: "UBER_DIRECT" | "RESTAURANT_MANAGED";
        fee: number;
    }[] | null;
    onlinePaymentGatewayProvider?: string | null;
    onlineDeliveryRestaurantContributionPercentage?: number | null;
    onlineDeliveryRestaurantContributionStatic?: number | null;
    onlineDeliveryTabinContributionPercentage?: number | null;
    onlineDeliveryTabinContributionStatic?: number | null;
    onlineDeliveryContributionMinimumAmount?: number | null;
    autoCompleteOrders: boolean | null;
    preparationTimeInMinutes: number | null;
    delayBetweenOrdersInSeconds: number | null;
    pauseOnlineOrdersUntil: string | null;
    pauseReservationsUntil: string | null;
    orderThresholdMessage: string | null;
    surchargePercentage: number | null;
    salesReportMailingList: string | null;
    orderThresholds: {
        enable: boolean;
    } | null;
    advertisements: { items: IGET_RESTAURANT_ADVERTISEMENT[] };
    thirdPartyIntegrations: IThirdPartyIntegrations | null;
    upSellCrossSell?: IGET_RESTAURANT_UP_SELL_CROSS_SELL;
    registers: { items: IGET_RESTAURANT_REGISTER[] };
    promotions: { items: IGET_RESTAURANT_PROMOTION[] };
    categories: {
        items: IGET_RESTAURANT_CATEGORY[];
    };
    // products: {
    //     items: IGET_RESTAURANT_PRODUCT[];
    // };
    // modifiers: {
    //     items: IGET_RESTAURANT_MODIFIER[];
    // };
}

export interface IThirdPartyIntegrations {
    enable: boolean | null;
    awaitThirdPartyResponse: boolean | null;
    shift8: IThirdPartyIntegrationsShift8 | null;
    wizBang: IThirdPartyIntegrationsWizBang | null;
    doshii: IThirdPartyIntegrationsDoshii | null;
}

export interface IThirdPartyIntegrationsShift8 {
    enable: boolean;
    storeApiUrl: string;
    storeUuid: string;
    storeLocationNumber: string;
}

export interface IThirdPartyIntegrationsWizBang {
    enable: boolean;
    storeApiUrl: string;
    username: string;
    password: string;
}

export interface IThirdPartyIntegrationsDoshii {
    enable: boolean;
    locationId: string;
}

export interface IGET_RESTAURANT_ADVERTISEMENT {
    id: string;
    name: string;
    content: IS3Object;
    availability: IGET_RESTAURANT_ADVERTISEMENT_AVAILABILITY_HOURS;
}

export interface IGET_RESTAURANT_ADVERTISEMENT_AVAILABILITY_HOURS {
    monday: IGET_RESTAURANT_ADVERTISEMENT_AVAILABILITY_TIMES[];
    tuesday: IGET_RESTAURANT_ADVERTISEMENT_AVAILABILITY_TIMES[];
    wednesday: IGET_RESTAURANT_ADVERTISEMENT_AVAILABILITY_TIMES[];
    thursday: IGET_RESTAURANT_ADVERTISEMENT_AVAILABILITY_TIMES[];
    friday: IGET_RESTAURANT_ADVERTISEMENT_AVAILABILITY_TIMES[];
    saturday: IGET_RESTAURANT_ADVERTISEMENT_AVAILABILITY_TIMES[];
    sunday: IGET_RESTAURANT_ADVERTISEMENT_AVAILABILITY_TIMES[];
    [key: string]: IGET_RESTAURANT_ADVERTISEMENT_AVAILABILITY_TIMES[]; //this is used to map over the operating hours object, https://www.logicbig.com/tutorials/misc/typescript/indexable-types.html
}

export interface IGET_RESTAURANT_ADVERTISEMENT_AVAILABILITY_TIMES {
    startTime: string;
    endTime: string;
}

export interface IGET_RESTAURANT_REGISTER {
    id: string;
    active: boolean;
    name: string;
    enableTableFlags: boolean;
    enableCovers: boolean;
    enableBuzzerNumbers: boolean;
    checkConditionsBeforeCreateOrder?: boolean;
    enablePayLater: boolean;
    enableCashPayments: boolean;
    enableEftposPayments: boolean;
    enableUberEatsPayments: boolean;
    enableMenulogPayments: boolean;
    availableOrderTypes: EOrderType[];
    type: ERegisterType;
    hideMostPopularCategory: boolean;
    orderBatchWindowInSeconds?: number | null;
    eftposProvider: string;
    eftposIpAddress: string;
    eftposPortNumber: string;
    windcaveStationId: string;
    windcaveStationUser: string;
    windcaveStationKey: string;
    skipEftposReceiptSignature: boolean;
    orderNumberSuffix: string;
    orderNumberStart: number;
    surchargePercentage: number;
    defaultCategoryView: string;
    preSelectedProducts: string[];
    customStyleSheet?: IS3Object;
    printers: {
        items: IGET_RESTAURANT_REGISTER_PRINTER[];
    };
}

export interface IGET_RESTAURANT_REGISTER_PRINTER {
    id: string;
    name: string;
    type: ERegisterPrinterType;
    printerType: EReceiptPrinterPrinterType;
    address: string;
    receiptFooterText: string | null;
    customerPrinter: boolean;
    kitchenPrinter: boolean;
    kitchenPrinterSmall: boolean;
    kitchenPrinterLarge: boolean;
    printAllOrderReceipts: boolean;
    printOnlineOrderReceipts: boolean;
    ignoreCategories: {
        items: IGET_RESTAURANT_REGISTER_PRINTER_IGNORE_CATEGORY[];
    };
    ignoreProducts: {
        items: IGET_RESTAURANT_REGISTER_PRINTER_IGNORE_PRODUCT[];
    };
}

export interface IGET_RESTAURANT_REGISTER_PRINTER_IGNORE_CATEGORY {
    id: string;
    category: {
        id: string;
        name: string;
    };
}

export interface IGET_RESTAURANT_REGISTER_PRINTER_IGNORE_PRODUCT {
    id: string;
    product: {
        id: string;
        name: string;
    };
}

export interface IGET_RESTAURANT_UP_SELL_CROSS_SELL {
    id: string;
    customCategories: {
        items: IGET_RESTAURANT_UP_SELL_CROSS_SELL_CUSTOM_CATEGORY[];
    };
    customProducts: {
        items: IGET_RESTAURANT_UP_SELL_CROSS_SELL_CUSTOM_PRODUCT[];
    };
}

export interface IGET_RESTAURANT_UP_SELL_CROSS_SELL_CUSTOM_CATEGORY {
    id: string;
}

export interface IGET_RESTAURANT_UP_SELL_CROSS_SELL_CUSTOM_PRODUCT {
    id: string;
    categories: {
        items: IGET_RESTAURANT_UP_SELL_CROSS_SELL_CUSTOM_PRODUCT_CATEGORY[];
    };
}

export interface IGET_RESTAURANT_UP_SELL_CROSS_SELL_CUSTOM_PRODUCT_CATEGORY {
    id: string;
}

export interface IGET_RESTAURANT_OPERATING_HOURS {
    sunday: IGET_RESTAURANT_OPERATING_HOURS_TIME_SLOT[];
    monday: IGET_RESTAURANT_OPERATING_HOURS_TIME_SLOT[];
    tuesday: IGET_RESTAURANT_OPERATING_HOURS_TIME_SLOT[];
    wednesday: IGET_RESTAURANT_OPERATING_HOURS_TIME_SLOT[];
    thursday: IGET_RESTAURANT_OPERATING_HOURS_TIME_SLOT[];
    friday: IGET_RESTAURANT_OPERATING_HOURS_TIME_SLOT[];
    saturday: IGET_RESTAURANT_OPERATING_HOURS_TIME_SLOT[];
}

export interface IGET_RESTAURANT_OPERATING_HOURS_TIME_SLOT {
    openingTime: string;
    closingTime: string;
    totalNumberOfOrdersThreshold: number | null;
    totalNumberOfOrdersThresholdPerTimeSlot: number | null;
    maxCoversPerTimeSlot: number | null;
}

export interface IGET_RESTAURANT_ITEM_AVAILABILITY_HOURS {
    monday: IGET_RESTAURANT_ITEM_AVAILABILITY_TIMES[];
    tuesday: IGET_RESTAURANT_ITEM_AVAILABILITY_TIMES[];
    wednesday: IGET_RESTAURANT_ITEM_AVAILABILITY_TIMES[];
    thursday: IGET_RESTAURANT_ITEM_AVAILABILITY_TIMES[];
    friday: IGET_RESTAURANT_ITEM_AVAILABILITY_TIMES[];
    saturday: IGET_RESTAURANT_ITEM_AVAILABILITY_TIMES[];
    sunday: IGET_RESTAURANT_ITEM_AVAILABILITY_TIMES[];
    [key: string]: IGET_RESTAURANT_ITEM_AVAILABILITY_TIMES[]; //this is used to map over the operating hours object, https://www.logicbig.com/tutorials/misc/typescript/indexable-types.html
}

export interface IGET_RESTAURANT_ITEM_AVAILABILITY_TIMES {
    startTime: string;
    endTime: string;
}

export interface IGET_RESTAURANT_PROMOTION {
    id: string;
    name: string;
    code: string;
    autoApply: boolean;
    startDate: string;
    endDate: string;
    availability: IGET_RESTAURANT_PROMOTION_AVAILABILITY;
    availablePlatforms: ERegisterType[];
    availableOrderTypes: EOrderType[] | null;
    totalNumberUsed: number;
    totalAvailableUses: number;
    minSpend: number;
    applyToCheapest: boolean;
    applyToModifiers: boolean;
    maxApplicationsPerOrder: number | null;
    type: EPromotionType;
    items: { items: IGET_RESTAURANT_PROMOTION_ITEMS[] };
    discounts: { items: IGET_RESTAURANT_PROMOTION_DISCOUNT[] };
    promotionRestaurantId: string;
    owner: string;
    createdAt: string;
    updatedAt: string;
}

export interface IGET_RESTAURANT_PROMOTION_AVAILABILITY {
    monday: IGET_RESTAURANT_PROMOTION_AVAILABILITY_TIMES[];
    tuesday: IGET_RESTAURANT_PROMOTION_AVAILABILITY_TIMES[];
    wednesday: IGET_RESTAURANT_PROMOTION_AVAILABILITY_TIMES[];
    thursday: IGET_RESTAURANT_PROMOTION_AVAILABILITY_TIMES[];
    friday: IGET_RESTAURANT_PROMOTION_AVAILABILITY_TIMES[];
    saturday: IGET_RESTAURANT_PROMOTION_AVAILABILITY_TIMES[];
    sunday: IGET_RESTAURANT_PROMOTION_AVAILABILITY_TIMES[];
}

export interface IGET_RESTAURANT_PROMOTION_AVAILABILITY_TIMES {
    startTime: string;
    endTime: string;
}

export enum EPromotionType {
    ENTIREORDER = "ENTIREORDER",
    COMBO = "COMBO",
    RELATEDITEMS = "RELATEDITEMS",
}

export interface IGET_RESTAURANT_PROMOTION_ITEMS {
    id: string;
    minQuantity: number;
    categoryIds: string[];
    productIds: string[];
}

export interface IGET_RESTAURANT_PROMOTION_DISCOUNT {
    id: string;
    amount: number;
    type: EDiscountType;
    items: { items: IGET_RESTAURANT_PROMOTION_ITEMS[] };
}

export enum EDiscountType {
    FIXED = "FIXED",
    PERCENTAGE = "PERCENTAGE",
    SETPRICE = "SETPRICE",
}

export interface IGET_RESTAURANT_CATEGORY {
    id: string;
    name: string;
    kitchenName: string | null;
    description: string;
    displaySequence: number;
    imageUrl?: string;
    image?: IS3Object;
    availablePlatforms: ERegisterType[];
    availableOrderTypes: EOrderType[] | null;
    soldOut?: boolean;
    soldOutDate?: string;
    availability: IGET_RESTAURANT_ITEM_AVAILABILITY_HOURS;
    products?: {
        items: IGET_RESTAURANT_PRODUCT_LINK[];
    };
}

export interface IGET_RESTAURANT_CATEGORY_LINK {
    category: IGET_RESTAURANT_CATEGORY;
}

export interface IGET_RESTAURANT_PRODUCT_LINK {
    id: string;
    displaySequence: number;
    product: IGET_RESTAURANT_PRODUCT;
}

export interface IGET_RESTAURANT_PRODUCT {
    id: string;
    name: string;
    kitchenName: string | null;
    description?: string;
    deliveryPrice?: number | null;
    price: number;
    displayPrice: string;
    tags: string | null;
    totalQuantitySold?: number;
    totalQuantityAvailable?: number;
    soldOut?: boolean;
    soldOutDate?: string;
    imageUrl: string;
    image?: IS3Object;
    availablePlatforms: ERegisterType[];
    availableOrderTypes: EOrderType[] | null;
    availability?: IGET_RESTAURANT_ITEM_AVAILABILITY_HOURS;
    subCategories?: string;
    reportingGroup?: string | null;
    categories: { items: IGET_RESTAURANT_CATEGORY_LINK[] };
    modifierGroups?: {
        items: IGET_RESTAURANT_MODIFIER_GROUP_LINK[];
    };
}

export interface IGET_RESTAURANT_MODIFIER_GROUP_LINK {
    id: string;
    displaySequence: number;
    modifierGroup: IGET_RESTAURANT_MODIFIER_GROUP;
}

export interface IGET_RESTAURANT_MODIFIER_GROUP {
    id: string;
    name: string;
    kitchenName: string | null;
    choiceMin: number;
    choiceMax: number;
    choiceDuplicate: number;
    collapsedByDefault?: boolean | null;
    hideForCustomer: boolean | null;
    availablePlatforms: ERegisterType[];
    availableOrderTypes: EOrderType[] | null;
    modifiers?: {
        items: IGET_RESTAURANT_MODIFIER_LINK[];
    };
}

export interface IGET_RESTAURANT_MODIFIER_LINK {
    id: string;
    displaySequence: number;
    modifier: IGET_RESTAURANT_MODIFIER;
}

export interface IGET_RESTAURANT_MODIFIER {
    id: string;
    name: string;
    kitchenName: string | null;
    description: string;
    deliveryPrice?: number | null;
    price: number;
    imageUrl: string;
    image?: IS3Object;
    totalQuantitySold?: number;
    totalQuantityAvailable?: number;
    soldOut?: boolean;
    soldOutDate?: string;
    availablePlatforms: ERegisterType[];
    availableOrderTypes: EOrderType[] | null;
    subModifierGroups: string;
    preSelectedQuantity: number;
    productModifier?: IGET_RESTAURANT_PRODUCT;
}

export interface IS3Object {
    key: string;
    bucket: string;
    region: string;
    identityPoolId: string;
}

export const GET_PROMOTION_BY_CODE = gql`
    query getPromotionsByCode($code: String!, $promotionRestaurantId: ID!) {
        getPromotionsByCode(code: $code, promotionRestaurantId: { eq: $promotionRestaurantId }) {
            items {
                id
                name
                type
                code
                autoApply
                startDate
                endDate
                availability {
                    monday {
                        startTime
                        endTime
                    }
                    tuesday {
                        startTime
                        endTime
                    }
                    wednesday {
                        startTime
                        endTime
                    }
                    thursday {
                        startTime
                        endTime
                    }
                    friday {
                        startTime
                        endTime
                    }
                    saturday {
                        startTime
                        endTime
                    }
                    sunday {
                        startTime
                        endTime
                    }
                }
                availablePlatforms
                availableOrderTypes
                totalNumberUsed
                totalAvailableUses
                minSpend
                applyToCheapest
                applyToModifiers
                maxApplicationsPerOrder
                items {
                    items {
                        id
                        minQuantity
                        categoryIds
                        productIds
                    }
                }
                discounts {
                    items {
                        id
                        amount
                        type
                        items {
                            items {
                                id
                                minQuantity
                                categoryIds
                                productIds
                            }
                        }
                    }
                }
            }
        }
    }
`;

export const GET_ORDERS_BY_RESTAURANT_BY_BEGIN_WITH_PLACEDAT = gql`
    ${ORDER_FIELDS_FRAGMENT}
    query GetOrdersByRestaurantByPlacedAt($orderRestaurantId: ID!, $placedAt: String!) {
        getOrdersByRestaurantByPlacedAt(
            limit: 1000000
            sortDirection: DESC
            orderRestaurantId: $orderRestaurantId
            placedAt: { beginsWith: $placedAt }
        ) {
            items {
                ...OrderFieldsFragment
            }
        }
    }
`;

export const GET_ORDERS_BY_RESTAURANT_BY_BETWEEN_PLACEDAT = gql`
    ${ORDER_FIELDS_FRAGMENT}
    query GetOrdersByRestaurantByPlacedAt($orderRestaurantId: ID!, $placedAtStartDate: String!, $placedAtEndDate: String!) {
        getOrdersByRestaurantByPlacedAt(
            limit: 1000000
            orderRestaurantId: $orderRestaurantId
            placedAt: { between: [$placedAtStartDate, $placedAtEndDate] }
        ) {
            items {
                ...OrderFieldsFragment
            }
        }
    }
`;

export const GET_ONLINE_ORDERS = gql`
    mutation getOnlineOrders($restaurantId: ID!, $scheduledAt: String) {
        getOnlineOrders(input: { restaurantId: $restaurantId, scheduledAt: $scheduledAt }) {
            orders {
                id
                placedAt
                orderScheduledAt
            }
        }
    }
`;

export interface IGET_ONLINE_ORDERS {
    id: string;
    placedAt: string;
    orderScheduledAt: string;
}

export const GET_PRODUCTS_BY_SKUCODE_BY_EQ_RESTAURANT = gql`
    query GetProductsBySKUCodeByRestaurant($skuCode: String!, $productRestaurantId: ID!) {
        getProductsBySKUCodeByRestaurant(limit: 1, sortDirection: DESC, skuCode: $skuCode, productRestaurantId: { eq: $productRestaurantId }) {
            items {
                id
                name
                description
                deliveryPrice
                price
                tags
                totalQuantitySold
                totalQuantityAvailable
                soldOut
                soldOutDate
                imageUrl
                image {
                    key
                    bucket
                    region
                    identityPoolId
                }
                availablePlatforms
                availability {
                    monday {
                        startTime
                        endTime
                    }
                    tuesday {
                        startTime
                        endTime
                    }
                    wednesday {
                        startTime
                        endTime
                    }
                    thursday {
                        startTime
                        endTime
                    }
                    friday {
                        startTime
                        endTime
                    }
                    saturday {
                        startTime
                        endTime
                    }
                    sunday {
                        startTime
                        endTime
                    }
                }
                subCategories
                reportingGroup
                categories {
                    items {
                        category {
                            id
                            name
                            imageUrl
                            image {
                                key
                                bucket
                                region
                                identityPoolId
                            }
                            displaySequence
                            availablePlatforms
                            availability {
                                monday {
                                    startTime
                                    endTime
                                }
                                tuesday {
                                    startTime
                                    endTime
                                }
                                wednesday {
                                    startTime
                                    endTime
                                }
                                thursday {
                                    startTime
                                    endTime
                                }
                                friday {
                                    startTime
                                    endTime
                                }
                                saturday {
                                    startTime
                                    endTime
                                }
                                sunday {
                                    startTime
                                    endTime
                                }
                            }
                        }
                    }
                }
                modifierGroups(limit: 500) {
                    items {
                        id
                        displaySequence
                        modifierGroup {
                            id
                            name
                            choiceMin
                            choiceMax
                            choiceDuplicate
                            collapsedByDefault
                            hideForCustomer
                            availablePlatforms
                            modifiers(limit: 500) {
                                items {
                                    id
                                    displaySequence
                                    modifier {
                                        preSelectedQuantity
                                        id
                                        name
                                        deliveryPrice
                                        price
                                        imageUrl
                                        image {
                                            key
                                            bucket
                                            region
                                            identityPoolId
                                        }
                                        totalQuantitySold
                                        totalQuantityAvailable
                                        soldOut
                                        soldOutDate
                                        availablePlatforms
                                        productModifier {
                                            id
                                            name
                                            description
                                            deliveryPrice
                                            price
                                            tags
                                            totalQuantitySold
                                            totalQuantityAvailable
                                            soldOut
                                            soldOutDate
                                            imageUrl
                                            image {
                                                key
                                                bucket
                                                region
                                                identityPoolId
                                            }
                                            categories {
                                                items {
                                                    category {
                                                        id
                                                        name
                                                        imageUrl
                                                        image {
                                                            key
                                                            bucket
                                                            region
                                                            identityPoolId
                                                        }
                                                    }
                                                }
                                            }
                                            availability {
                                                monday {
                                                    startTime
                                                    endTime
                                                }
                                                tuesday {
                                                    startTime
                                                    endTime
                                                }
                                                wednesday {
                                                    startTime
                                                    endTime
                                                }
                                                thursday {
                                                    startTime
                                                    endTime
                                                }
                                                friday {
                                                    startTime
                                                    endTime
                                                }
                                                saturday {
                                                    startTime
                                                    endTime
                                                }
                                                sunday {
                                                    startTime
                                                    endTime
                                                }
                                            }
                                            modifierGroups(limit: 500) {
                                                items {
                                                    id
                                                    displaySequence
                                                    modifierGroup {
                                                        id
                                                        name
                                                        choiceMin
                                                        choiceMax
                                                        choiceDuplicate
                                                        collapsedByDefault
                                                        hideForCustomer
                                                        availablePlatforms
                                                        modifiers(limit: 500) {
                                                            items {
                                                                id
                                                                displaySequence
                                                                modifier {
                                                                    preSelectedQuantity
                                                                    id
                                                                    name
                                                                    deliveryPrice
                                                                    price
                                                                    imageUrl
                                                                    image {
                                                                        key
                                                                        bucket
                                                                        region
                                                                        identityPoolId
                                                                    }
                                                                    totalQuantitySold
                                                                    totalQuantityAvailable
                                                                    soldOut
                                                                    soldOutDate
                                                                    availablePlatforms
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`;

export const GET_RESTAURANT_AVAILABILITY = gql`
    query GetRestaurantAvailability($restaurantId: ID!) {
        getRestaurant(id: $restaurantId) {
            id
            categories(limit: 20) {
                items {
                    id
                    soldOut
                    soldOutDate
                    products(limit: 100) {
                        items {
                            product {
                                id
                                name
                                soldOut
                                soldOutDate
                                totalQuantityAvailable
                                modifierGroups(limit: 20) {
                                    items {
                                        modifierGroup {
                                            id
                                            name
                                            choiceMin
                                            modifiers(limit: 50) {
                                                items {
                                                    modifier {
                                                        id
                                                        name
                                                        soldOut
                                                        soldOutDate
                                                        totalQuantityAvailable
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`;

export interface IGET_RESTAURANT_AVAILABILITY {
    getRestaurant: IGET_RESTAURANT_AVAILABILITY_RESTAURANT;
}

export interface IGET_RESTAURANT_AVAILABILITY_RESTAURANT {
    id: string;
    categories: {
        items: IGET_RESTAURANT_AVAILABILITY_CATEGORY[];
    };
}

export interface IGET_RESTAURANT_AVAILABILITY_CATEGORY {
    id: string;
    soldOut: boolean | null;
    soldOutDate: string | null;
    products: {
        items: IGET_RESTAURANT_AVAILABILITY_PRODUCT_LINK[];
    };
}

export interface IGET_RESTAURANT_AVAILABILITY_PRODUCT_LINK {
    product: IGET_RESTAURANT_AVAILABILITY_PRODUCT;
}

export interface IGET_RESTAURANT_AVAILABILITY_PRODUCT {
    id: string;
    name: string;
    soldOut: boolean | null;
    soldOutDate: string | null;
    totalQuantityAvailable: number | null;
    modifierGroups: {
        items: IGET_RESTAURANT_AVAILABILITY_MODIFIER_GROUP_LINK[];
    };
}

export interface IGET_RESTAURANT_AVAILABILITY_MODIFIER_GROUP_LINK {
    modifierGroup: IGET_RESTAURANT_AVAILABILITY_MODIFIER_GROUP;
}

export interface IGET_RESTAURANT_AVAILABILITY_MODIFIER_GROUP {
    id: string;
    name: string;
    choiceMin: number | null;
    modifiers: {
        items: IGET_RESTAURANT_AVAILABILITY_MODIFIER_LINK[];
    };
}

export interface IGET_RESTAURANT_AVAILABILITY_MODIFIER_LINK {
    modifier: IGET_RESTAURANT_AVAILABILITY_MODIFIER;
}

export interface IGET_RESTAURANT_AVAILABILITY_MODIFIER {
    id: string;
    name: string;
    soldOut: boolean | null;
    soldOutDate: string | null;
    totalQuantityAvailable: number | null;
}

export enum EReservationStatus {
    PENDING = "PENDING",
    CONFIRMED = "CONFIRMED",
    SEATED = "SEATED",
    COMPLETED = "COMPLETED",
    CANCELLED = "CANCELLED",
    NO_SHOW = "NO_SHOW",
}

export interface IGET_RESERVATION {
    id: string;
    restaurantId: string;
    date: string;
    time: string;
    covers: number;
    status: EReservationStatus;
    customerName: string;
    customerEmail: string | null;
    customerPhone: string | null;
    notes: string | null;
}

export const GET_RESERVATIONS_BY_RESTAURANT_BY_DATE = gql`
    query GetReservationsByRestaurantByDate($restaurantId: ID!, $date: ModelStringKeyConditionInput, $limit: Int, $nextToken: String) {
        getReservationsByRestaurantByDate(restaurantId: $restaurantId, date: $date, limit: $limit, nextToken: $nextToken) {
            items {
                id
                restaurantId
                date
                time
                covers
                status
            }
            nextToken
        }
    }
`;

export const GET_RESERVATION_PAUSE_STATUS = gql`
    query GetReservationPauseStatus($restaurantId: ID!) {
        getRestaurant(id: $restaurantId) {
            id
            pauseReservationsUntil
        }
    }
`;
